import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Check, Sparkles, Crown, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Packages() {
  const { refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [econ, setEcon] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/packages").then(({ data }) => { setPackages(data.packages); setEcon(data.economics); }); }, []);

  const startCheckout = async (pkg) => {
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg.id });
      setTx(data); setCheckout(pkg);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const pay = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/confirm/${tx.transaction_id}`);
      await refresh();
      toast.success("Payment successful! Credits added.");
      setCheckout(null); setTx(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const passes = packages.filter((p) => p.type === "pass");
  const bundles = packages.filter((p) => p.type === "bundle");
  const membership = packages.find((p) => p.type === "membership");

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal">Time, well spent.</h1>
          <p className="text-charcoal/60 mt-3">Buy time credits, spent in 1-3 hour public hangouts. Prices in INR.</p>
          <div className="inline-flex items-center gap-2 bg-sun/20 border border-sun rounded-full px-4 py-1.5 text-xs font-bold text-charcoal mt-4"><CreditCard className="w-4 h-4" /> Demo checkout — Razorpay (INR) plugs in with your keys</div>
        </div>

        <h2 className="font-display text-2xl font-bold text-charcoal mt-12 mb-5">Time passes</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {passes.map((p) => (
            <PkgCard key={p.id} p={p} onBuy={() => startCheckout(p)} highlight={p.badge === "Most Popular"} />
          ))}
        </div>

        <h2 className="font-display text-2xl font-bold text-charcoal mt-12 mb-5">Bundles</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {bundles.map((p) => (
            <PkgCard key={p.id} p={p} onBuy={() => startCheckout(p)} premium={p.badge === "Premium"} />
          ))}
        </div>

        {membership && (
          <div className="bg-charcoal rounded-3xl p-8 md:p-10 mt-12 flex items-center justify-between flex-wrap gap-6">
            <div>
              <span className="inline-flex items-center gap-2 text-sun text-xs font-bold uppercase tracking-wider"><Crown className="w-4 h-4" /> Membership</span>
              <h3 className="font-display text-3xl font-bold text-white mt-2">{membership.name}</h3>
              <p className="text-white/70 mt-1">{membership.desc}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-4xl font-black text-white">₹{membership.price}<span className="text-lg font-normal text-white/60">/mo</span></p>
              <Button data-testid={`buy-${membership.id}`} onClick={() => startCheckout(membership)} className="rounded-full bg-sun text-charcoal hover:bg-sun/90 mt-3 px-7 h-11">Become a member</Button>
            </div>
          </div>
        )}

        {econ && (
          <div className="mt-12 bg-white rounded-2xl border border-border p-6">
            <h3 className="font-display text-lg font-bold text-charcoal">How the money works</h3>
            <div className="grid grid-cols-3 gap-4 mt-4 text-center">
              <div><p className="font-display text-2xl font-bold text-sage">{econ.host_payout_pct}%</p><p className="text-xs text-charcoal/60">to your host</p></div>
              <div><p className="font-display text-2xl font-bold text-terracotta">{econ.commission_pct}%</p><p className="text-xs text-charcoal/60">platform commission</p></div>
              <div><p className="font-display text-2xl font-bold text-charcoal/50">{econ.gateway_ops_pct}%</p><p className="text-xs text-charcoal/60">gateway & ops</p></div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!checkout} onOpenChange={(o) => !o && setCheckout(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Checkout</DialogTitle></DialogHeader>
          {checkout && (
            <div className="space-y-4">
              <div className="bg-sand rounded-xl p-4 flex items-center justify-between">
                <div><p className="font-semibold text-charcoal">{checkout.name}</p><p className="text-xs text-charcoal/60">{checkout.desc}</p></div>
                <p className="font-display text-2xl font-bold text-charcoal">₹{checkout.price}</p>
              </div>
              <div className="text-sm text-charcoal/70 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sage" /> Funds held until your session completes. GST invoice issued.</div>
              <div className="bg-sun/15 border border-sun rounded-xl p-3 text-xs text-charcoal">DEMO payment. In production this routes to a licensed gateway (Razorpay/UPI).</div>
              <Button data-testid="pay-now" onClick={pay} disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">
                {busy ? "Processing..." : `Pay ₹${checkout.price} (demo)`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PkgCard({ p, onBuy, highlight, premium }) {
  return (
    <div className={`relative rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${highlight ? "bg-white border-terracotta ring-2 ring-terracotta/30" : premium ? "bg-charcoal border-charcoal text-white" : "bg-white border-border"}`}>
      {p.badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full ${premium ? "bg-sun text-charcoal" : "bg-terracotta text-white"}`}>{p.badge}</span>}
      <h3 className={`font-display text-2xl font-bold ${premium ? "text-white" : "text-charcoal"}`}>{p.name}</h3>
      <p className={`text-sm mt-1 ${premium ? "text-white/70" : "text-charcoal/60"}`}>{p.desc}</p>
      <p className={`font-display text-4xl font-black mt-5 ${premium ? "text-white" : "text-charcoal"}`}>₹{p.price}</p>
      <p className={`text-sm ${premium ? "text-white/60" : "text-charcoal/50"}`}>{p.minutes / 60} hours of credits</p>
      {p.perks && (
        <ul className="mt-4 space-y-2">
          {p.perks.map((perk) => (
            <li key={perk} className={`flex items-center gap-2 text-sm ${premium ? "text-white/80" : "text-charcoal/70"}`}><Check className={`w-4 h-4 ${premium ? "text-sun" : "text-sage"}`} /> {perk}</li>
          ))}
        </ul>
      )}
      <Button data-testid={`buy-${p.id}`} onClick={onBuy} className={`w-full mt-6 rounded-full h-11 ${premium ? "bg-sun text-charcoal hover:bg-sun/90" : "bg-terracotta text-white hover:bg-terracotta/90"}`}>Buy now</Button>
    </div>
  );
}
